/**
 * Отдельный vuex модуль для взаимодействия по analytics/parking_detection-API
 */

import {ANALYTICS, AnalyticSettings} from "@/store/analytics/helpers.js";
import makeMessage from "@/store/analytics/analyticMessage.js";
import {
  ACTION_LOAD_CAMERAS,
  CAMERA_SCREENSHOT_SIZES,
  FIELDS_CAMERA,
} from "@/store/cameras/index.js";
import {TOKEN_TTL} from "@/utils/consts.js";

// actions для parking_detections. Вызов действия начинать с "analytics/parkingDetections/"
export const ACTION_LOAD_PARKING_DETECTIONS_SETTINGS = "LOAD_PARKING_DETECTIONS_SETTINGS";
export const ACTION_LOAD_PARKING_DETECTION_SETTINGS = "LOAD_PARKING_DETECTION_SETTINGS";
export const ACTION_LOAD_PARKING_DETECTION_EVENTS = "LOAD_PARKING_DETECTION_EVENTS";
export const ACTION_LOAD_PARKING_DETECTION_EVENT = "LOAD_PARKING_DETECTION_EVENT";
export const ACTION_LOAD_LAST_PARKING_DETECTION_EVENT = "LOAD_LAST_PARKING_DETECTION_EVENT";
export const ACTION_LOAD_PARKING_DETECTION_REPORT = "LOAD_PARKING_DETECTION_REPORT";
export const ACTION_LOAD_PARKING_AREAS = "LOAD_PARKING_AREAS";
export const ACTION_LOAD_PARKINGS = "LOAD_PARKINGS";
export const ACTION_CREATE_PARKING = "CREATE_PARKING";
export const ACTION_EDIT_PARKING = "EDIT_PARKING";
export const ACTION_DELETE_PARKINGS = "DELETE_PARKINGS";
export const ACTION_CREATE_PARKING_AREA = "CREATE_PARKING_AREA";
export const ACTION_EDIT_PARKING_AREA = "EDIT_PARKING_AREA";
export const ACTION_DELETE_PARKING_AREAS = "DELETE_PARKING_AREAS";
export const ACTION_SET_PARKING_AREA_PARKINGS = "SET_PARKING_AREA_PARKINGS";
export const ACTION_LOAD_PARKING_CAMERAS = "LOAD_PARKING_CAMERAS";
export const ACTION_LOAD_PARKING_ANALYTICS_CAMERAS = "LOAD_PARKING_ANALYTICS_CAMERAS";

const PARKING_DETECTION_API_ROOT = "/v0/analytics/parking_detection";
const PARKING_ANALYTICS_EDIT_ACTION = "analytics/LOAD_PARKING_DETECTION_ANALYTICS_FOR_EDIT";

const makeParkingApiError = error => {
  const responseData = error && error.response && error.response.data;
  const message = (responseData && responseData.error)
    || (error && error.message)
    || "Не удалось выполнить запрос";
  const requestError = new Error(message);

  requestError.fields = responseData && responseData.fields;
  requestError.responseData = responseData;
  return requestError;
};

const postParkingApi = async (client, endpoint, payload) => {
  try {
    const apiUrl = endpoint
      ? `${PARKING_DETECTION_API_ROOT}/${endpoint}/`
      : `${PARKING_DETECTION_API_ROOT}/`;
    const response = await client.post(
      apiUrl,
      payload
    );
    return response.data;
  } catch (error) {
    throw makeParkingApiError(error);
  }
};

const uniqueCameraNumbers = cameraNumbers => [...new Set(
  (Array.isArray(cameraNumbers) ? cameraNumbers : [])
    .map(cameraNumber => String(cameraNumber || ""))
    .filter(Boolean)
)];

/**
 * Класс для представления настроек аналитики детекции парковок.
 */
export class PerimeterSecuritySettings extends AnalyticSettings {
  get analyticType() {
    return ANALYTICS.parking_detection;
  }
}

export default {
  namespaced: true,
  state: {},
  mutations: {},
  actions: {
    /** Загрузка номеров всех камер с настроенной аналитикой парковочных мест. */
    async [ACTION_LOAD_PARKING_ANALYTICS_CAMERAS](context, payload) {
      const results = [];
      let page = payload.page || 1;
      let response;

      do {
        response = await postParkingApi(this.getters.ajaxClient, "", {...payload, page});
        results.push(...(response.results || []));
        page = response.page && response.page.next;
      } while (page);

      return {...response, results};
    },
    /** Загрузка парковочных комплексов. */
    async [ACTION_LOAD_PARKING_AREAS](context, payload) {
      return postParkingApi(this.getters.ajaxClient, "parking_area", payload);
    },
    /** Загрузка парковок. */
    async [ACTION_LOAD_PARKINGS](context, payload) {
      return postParkingApi(this.getters.ajaxClient, "parking", payload);
    },
    /** Создание парковки. */
    async [ACTION_CREATE_PARKING](context, payload) {
      return postParkingApi(this.getters.ajaxClient, "parking/create", payload);
    },
    /** Редактирование парковки. */
    async [ACTION_EDIT_PARKING](context, payload) {
      return postParkingApi(this.getters.ajaxClient, "parking/edit", payload);
    },
    /** Удаление парковок. */
    async [ACTION_DELETE_PARKINGS](context, {ids, comment = ""}) {
      return postParkingApi(this.getters.ajaxClient, "parking/delete", {ids, comment});
    },
    /** Создание парковочного комплекса. */
    async [ACTION_CREATE_PARKING_AREA](context, payload) {
      return postParkingApi(this.getters.ajaxClient, "parking_area/create", payload);
    },
    /** Редактирование парковочного комплекса. */
    async [ACTION_EDIT_PARKING_AREA](context, payload) {
      return postParkingApi(this.getters.ajaxClient, "parking_area/edit", payload);
    },
    /** Удаление парковочных комплексов. */
    async [ACTION_DELETE_PARKING_AREAS](context, {ids, comment = ""}) {
      return postParkingApi(this.getters.ajaxClient, "parking_area/delete", {ids, comment});
    },
    /** Полная синхронизация парковок, привязанных к комплексу. */
    async [ACTION_SET_PARKING_AREA_PARKINGS](context, {
      parkingAreaId,
      parkingIds,
      comment = "",
    }) {
      return postParkingApi(this.getters.ajaxClient, "parking_area/parking/set", {
        parking_area_id: parkingAreaId,
        parking_ids: parkingIds,
        comment,
      });
    },
    /**
     * Загрузка карточек камер по их номерам и сведений о разметке парковочных мест.
     */
    async [ACTION_LOAD_PARKING_CAMERAS]({dispatch}, {
      cameraNumbers = [],
      includeMarkup = true,
    }) {
      const numbers = uniqueCameraNumbers(cameraNumbers);
      if (!numbers.length) return [];

      const cameraInfos = await dispatch(`cameras/${ACTION_LOAD_CAMERAS}`, {
        fields: [
          FIELDS_CAMERA.number,
          FIELDS_CAMERA.address,
          FIELDS_CAMERA.title,
          FIELDS_CAMERA.longitude,
          FIELDS_CAMERA.latitude,
          FIELDS_CAMERA.is_fav,
          FIELDS_CAMERA.server,
          FIELDS_CAMERA.token_l,
        ],
        tokenLiveTTL: TOKEN_TTL.DEFAULT,
        numbers,
      }, {root: true});
      const cameraInfoByNumber = new Map(
        cameraInfos.map(camera => [String(camera.number), camera])
      );
      const markupResults = includeMarkup
        ? await Promise.all(numbers.map(async cameraNumber => {
          try {
            const response = await dispatch(
              PARKING_ANALYTICS_EDIT_ACTION,
              cameraNumber,
              {root: true}
            );
            return response && response.entityInfo ? response.entityInfo : {};
          } catch {
            return {};
          }
        }))
        : numbers.map(() => ({}));

      return numbers.map((cameraNumber, index) => {
        const camera = cameraInfoByNumber.get(cameraNumber);
        const markup = markupResults[index] || {};
        const parkingSpots = Array.isArray(markup.parking_spots)
          ? markup.parking_spots
          : [];

        return {
          camera_number: cameraNumber,
          number: cameraNumber,
          title: camera ? camera.title : cameraNumber,
          address: camera ? camera.address : "",
          latitude: camera ? camera.latitude : null,
          longitude: camera ? camera.longitude : null,
          is_favorite: camera ? Boolean(camera.inFavorites) : false,
          preview_url: camera
            && camera.server
            && camera.server.screenshot_domain
            && camera.tokenLive
            ? camera.screenshotUrl(CAMERA_SCREENSHOT_SIZES.s400)
            : "",
          image_width: markup.image_width,
          image_height: markup.image_height,
          parking_spots: parkingSpots,
          parking_spots_count: parkingSpots.length,
          is_markup_complete: parkingSpots.length > 0,
        };
      });
    },
    /**
     * Загрузка списка настроек аналитик детекции парковок.
     *
     * @param {Object} context
     * @param {Array.<String>} camerasNumbers
     * @return {Promise.<Array.<PerimeterSecuritySettings>>}
     */
    async [ACTION_LOAD_PARKING_DETECTIONS_SETTINGS](context, {camerasNumbers}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/parking_detection/settings/", {camera_numbers: camerasNumbers});
        return response.data.results.map(rawPerimeterSecuritySettings => new PerimeterSecuritySettings(rawPerimeterSecuritySettings));
      } catch {
        return [];
      }
    },
    /**
     * Загрузка настроек аналитики детекции парковок с конкретной камеры.
     *
     * @param {Function} dispatch
     * @param {String} cameraNumber
     * @return {Promise.<PerimeterSecuritySettings>}
     */
    async [ACTION_LOAD_PARKING_DETECTION_SETTINGS]({dispatch}, {cameraNumber}) {
      const parkingDetectionsSettings = await dispatch(ACTION_LOAD_PARKING_DETECTIONS_SETTINGS, {camerasNumbers: [cameraNumber]});
      if (_.isEmpty(parkingDetectionsSettings)) {
        return null;
      }
      return parkingDetectionsSettings[0];
    },
    /**
     * Загрузка перечня событий по аналитике детекции парковок с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {String} cameraNumber
     * @param {Array.<Number>} eventIds
     * @return {Promise.<Array.<PerimeterSecurityMessage>>}
     */
    async [ACTION_LOAD_PARKING_DETECTION_EVENTS](context, {cameraNumber, eventIds}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/parking_detection/events/", {
          camera_number: cameraNumber,
          event_ids: eventIds,
        });
        return response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.parking_detection));
      } catch {
        return [];
      }
    },
    /**
     * Загрузка перечня событий по аналитике детекции парковок с камеры для формирования отчета.
     *
     * @param {Function} dispatch
     * @param {String} cameraNumber
     * @param {Number} eventId
     * @return {Promise.<PerimeterSecurityMessage>}
     */
    async [ACTION_LOAD_PARKING_DETECTION_EVENT]({dispatch}, {cameraNumber, eventId}) {
      const parkingDetectionsSettings = await dispatch(ACTION_LOAD_PARKING_DETECTION_EVENTS, {cameraNumber, eventIds: [eventId]});
      if (_.isEmpty(parkingDetectionsSettings)) {
        return null;
      }
      return parkingDetectionsSettings[0];
    },
    /**
     * Загрузка последнего состояния парковочных мест на указанную дату.
     *
     * @param {Object} context
     * @param {String} cameraNumber
     * @param {Date|String} date
     * @return {Promise.<ParkingDetectionMessage>}
     */
    async [ACTION_LOAD_LAST_PARKING_DETECTION_EVENT](context, {cameraNumber, date = new Date()}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/parking_detection/events/last/", {
          camera_number: cameraNumber,
          date: date instanceof Date ? date.toISOString() : date,
        });
        const rawResults = response.data.results || response.data,
          rawLastEvent = Array.isArray(rawResults) ? rawResults[0] : rawResults;
        return rawLastEvent ? makeMessage(rawLastEvent, cameraNumber, ANALYTICS.parking_detection) : null;
      } catch {
        return null;
      }
    },
    /**
     * Загрузка перечня событий по определению детекции парковок с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {String} cameraNumber
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @param {String} query
     * @return {Promise.<Array.<PerimeterSecurityMessage>>}
     */
    async [ACTION_LOAD_PARKING_DETECTION_REPORT](context, {cameraNumber, archiveFrom, archiveTo, query = null, page, page_size, cancelToken}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/parking_detection/report/", {
          camera_number: cameraNumber,
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          query: query,
          page: page,
          page_size: page_size
        }, {cancelToken});
        const messages = response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.parking_detection));

        return {
          messages,
          response: response.data
        };
      } catch {
        return {
          messages: [],
          response: null
        };
      }
    },
  },
};
